<script lang="ts">
  import { onMount } from 'svelte';
  import { tweened } from 'svelte/motion';
  import { cubicOut } from 'svelte/easing';

  export let scale: number = 1;
  export let color: string = 'var(--theme-primary-color)';
  export let duration: number = 2000; // Duration for the initial pulsating phase
  export let finalSize: number = 80; // Final size of the circle in pixels
  export let submissionPromise: Promise<any> | null = null; // Promise to track submission
  export let showOnSuccess: boolean = true; // Whether to show checkmark on success

  // Animation state
  let checkmarkVisible = false;
  let errorVisible = false;
  let isGrown = false; // Control the grown class for animation stop
  let submissionComplete = false;

  // Motion type: width, height, opacity
  type BubbleMotion = {
    width: number;
    height: number;
    opacity: number;
  };

  // Initial size
  const initialSize = 25;

  // Tween for size and opacity
  const bubbleMotion = tweened<BubbleMotion>(
    { width: initialSize, height: initialSize, opacity: 0.7 },
    { duration: 600, easing: cubicOut }
  );

  // Handle submission promise
  async function handleSubmission() {
    if (!submissionPromise) {
      // If no submission promise, use the original behavior with duration
      setTimeout(() => {
        triggerSuccess();
      }, duration);
      return;
    }

    try {
      await submissionPromise;
      // Submission successful
      if (showOnSuccess) {
        triggerSuccess();
      }
    } catch (error) {
      // Submission failed
      triggerError();
    }
  }

  // Trigger success animation
  function triggerSuccess() {
    submissionComplete = true;
    isGrown = true;
    bubbleMotion.set({ 
      width: finalSize, 
      height: finalSize, 
      opacity: 1 
    }, { 
      duration: 600, 
      easing: cubicOut 
    }).then(() => {
      checkmarkVisible = true;
    });
  }

  // Trigger error animation
  function triggerError() {
    submissionComplete = true;
    isGrown = true;
    bubbleMotion.set({ 
      width: finalSize, 
      height: finalSize, 
      opacity: 1 
    }, { 
      duration: 600, 
      easing: cubicOut 
    }).then(() => {
      errorVisible = true;
    });
  }

  // Start animation on mount
  onMount(() => {
    handleSubmission();
  });

  // Motion subscription
  $: motion = $bubbleMotion;

  // Update color for error state
  $: bubbleColor = errorVisible ? '#ef4444' : color; // Red for error, original color for success
</script>

<div class="container" style:--scale={scale} style:--color={bubbleColor} style:--final-size={finalSize}px>
  <div class="bubble-container">
    <div
      class="bubble"
      class:grown={isGrown}
      style:width="{motion.width}px"
      style:height="{motion.height}px"
      style:opacity={motion.opacity}
    ></div>

    {#if checkmarkVisible}
      <svg class="checkmark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52">
        <path class="checkmark__check" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8"/>
      </svg>
    {/if}

    {#if errorVisible}
      <svg class="error-mark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52">
        <path class="error-mark__cross" fill="none" d="M16 16l20 20M36 16L16 36"/>
      </svg>
    {/if}
  </div>
</div>

<style>
  .container {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    transform: scale(var(--scale)); /* Outer container scale */
  }

  .bubble-container {
    width: var(--final-size); /* Use CSS var */
    height: var(--final-size);
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .bubble {
    /* width/height/opacity controlled by tween */
    background-color: var(--color);
    border-radius: 50%;
    position: absolute;
    transform-origin: center center;
    animation: pulse 0.8s ease-in-out infinite;
    transition: background-color 0.6s ease-in-out; /* Only transition color if needed */
  }

 .bubble.grown {
    animation: none; /* Stop pulsing */
    /* Final size/opacity set by tween */
  }

  /* Checkmark styles (slightly refined animations) */
  .checkmark {
    width: calc(var(--final-size) * 0.76);
    height: calc(var(--final-size) * 0.76);
    position: absolute;
    stroke-width: 5;
    stroke: white;
    stroke-miterlimit: 10;
    opacity: 0; /* Start hidden */
    /* Combined animation: fade in, then scale pop */
    animation: checkmarkAppear 0.3s ease-out forwards,
               checkmarkScale 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275) 0.2s forwards; /* Scale after appearing */
  }

  .checkmark__check {
    transform-origin: 50% 50%;
    stroke-dasharray: 48;
    stroke-dashoffset: 48;
    animation: stroke 0.3s cubic-bezier(0.65, 0, 0.45, 1) 0.1s forwards; /* Start drawing slightly after container appears */
  }

  /* Error mark styles */
  .error-mark {
    width: calc(var(--final-size) * 0.76);
    height: calc(var(--final-size) * 0.76);
    position: absolute;
    stroke-width: 5;
    stroke: white;
    stroke-miterlimit: 10;
    opacity: 0; /* Start hidden */
    /* Combined animation: fade in, then scale pop */
    animation: checkmarkAppear 0.3s ease-out forwards,
               checkmarkScale 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275) 0.2s forwards; /* Scale after appearing */
  }

  .error-mark__cross {
    transform-origin: 50% 50%;
    stroke-dasharray: 56; /* Slightly longer for X shape */
    stroke-dashoffset: 56;
    animation: stroke 0.3s cubic-bezier(0.65, 0, 0.45, 1) 0.1s forwards; /* Start drawing slightly after container appears */
  }

  @keyframes pulse {
    0%, 100% {
      transform: scale(0.6);
      /* Opacity handled by tween */
    }
    50% {
      transform: scale(1);
      /* Opacity handled by tween */
    }
  }

  @keyframes checkmarkAppear {
     to {
        opacity: 1;
     }
  }

   @keyframes checkmarkScale {
     0% { transform: scale(0.8); }
     80% { transform: scale(1.1); }
     100% { transform: scale(1); }
  }

  @keyframes stroke {
    100% {
      stroke-dashoffset: 0;
    }
  }
</style> 